import type { WidgetDefinition } from "../types";
import { STANDARD_SIZES as ST } from "../types";

import {
  IconAltitude,
  IconAltitudeLine,
  IconBrain,
  IconChart,
  IconClock,
  IconCompass,
  IconDrone,
  IconFlag,
  IconFlame,
  IconHeatmap,
  IconList,
  IconMissionProgress,
  IconRadar,
  IconServer,
  IconTrend,
  IconTrophy,
} from "./icons";
import { ActivityChartWidget } from "./ActivityChartWidget";
import { ActivityHeatmapWidget } from "./ActivityHeatmapWidget";
import { AiFeedbackWidget } from "./AiFeedbackWidget";
import { AltitudeProfileWidget } from "./AltitudeProfileWidget";
import { MissionProgressWidget } from "./MissionProgressWidget";
import { SkillRadarWidget } from "./SkillRadarWidget";
import {
  FlightCountWidget,
  LongestFlightWidget,
  MaxAltitudeWidget,
  MissionCountWidget,
  StreakWidget,
  TotalDistanceWidget,
  TotalHoursWidget,
} from "./KpiWidgets";
import { LastFlightWidget } from "./LastFlightWidget";
import { LeaderboardWidget } from "./LeaderboardWidget";
import { PerformanceTrendsWidget } from "./PerformanceTrendsWidget";
import { RecentFlightsWidget } from "./RecentFlightsWidget";
import { SystemStatusWidget } from "./SystemStatusWidget";

// Grid: 12 cols at lg/md, 4 at sm. Row height = 72px.
// Sizes use the standardized XS/S/M/L tiers defined in ../types.ts:
//   XS 3×2 · S 3×4 · M 6×4 · L 12×5
// Widgets may opt in to any subset that makes visual sense for them.

export const HOME_WIDGETS: WidgetDefinition[] = [
  // --- Hero / overview widgets ---
  {
    type: "last_flight",
    title: "Last flight",
    description: "Most recent mission with stats, smoothness, AI feedback.",
    category: "overview",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "l",
    Icon: IconDrone,
    Component: LastFlightWidget,
  },

  // --- KPIs — use XS (compact) or S (with sparkline/context) ---
  {
    type: "flight_count",
    title: "Flights",
    description: "Total flight count. S adds a 7-day trend sparkline.",
    category: "overview",
    sizes: { xs: ST.xs, s: ST.s },
    defaultSize: "xs",
    Icon: IconDrone,
    Component: FlightCountWidget,
  },
  {
    type: "total_hours",
    title: "Total time",
    description: "Accumulated flight time. S adds avg per flight.",
    category: "overview",
    sizes: { xs: ST.xs, s: ST.s },
    defaultSize: "xs",
    Icon: IconClock,
    Component: TotalHoursWidget,
  },
  {
    type: "total_distance",
    title: "Distance",
    description: "Total distance flown.",
    category: "overview",
    sizes: { xs: ST.xs },
    defaultSize: "xs",
    Icon: IconCompass,
    Component: TotalDistanceWidget,
  },
  {
    type: "max_altitude",
    title: "Max altitude",
    description: "All-time altitude peak.",
    category: "overview",
    sizes: { xs: ST.xs },
    defaultSize: "xs",
    Icon: IconAltitude,
    Component: MaxAltitudeWidget,
  },
  {
    type: "mission_count",
    title: "Missions",
    description: "Unique missions and maps flown.",
    category: "overview",
    sizes: { xs: ST.xs },
    defaultSize: "xs",
    Icon: IconFlag,
    Component: MissionCountWidget,
  },
  {
    type: "streak",
    title: "Streak",
    description: "Consecutive days with a flight. S adds 14-day sparkline.",
    category: "activity",
    sizes: { xs: ST.xs, s: ST.s },
    defaultSize: "xs",
    Icon: IconFlame,
    Component: StreakWidget,
  },
  {
    type: "longest_flight",
    title: "Longest flight",
    description: "Your personal longest continuous flight.",
    category: "performance",
    sizes: { xs: ST.xs },
    defaultSize: "xs",
    Icon: IconClock,
    Component: LongestFlightWidget,
  },

  // --- Activity ---
  {
    type: "activity_heatmap",
    title: "Activity heatmap",
    description: "Daily flight activity with range toggle (1w / 1m / 3m / 6m / 1y).",
    category: "activity",
    kind: "chart",
    sizes: { m: ST.m, l: ST.l },
    defaultSize: "l",
    Icon: IconHeatmap,
    Component: ActivityHeatmapWidget,
  },
  {
    type: "activity_chart",
    title: "Weekly activity",
    description: "Flight volume bar chart with trend line.",
    category: "activity",
    kind: "chart",
    sizes: { m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconChart,
    Component: ActivityChartWidget,
  },

  // --- Lists & feedback ---
  {
    type: "recent_flights",
    title: "Recent flights",
    description: "Latest flights as mini list, table, or filtered table.",
    category: "overview",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconList,
    Component: RecentFlightsWidget,
  },
  {
    type: "performance_trends",
    title: "Performance trends",
    description: "Smoothness, speed, altitude with flight picker.",
    category: "performance",
    kind: "chart",
    sizes: { m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconTrend,
    Component: PerformanceTrendsWidget,
  },
  {
    type: "ai_feedback",
    title: "AI feedback",
    description: "AI coaching on your most recent flight.",
    category: "performance",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconBrain,
    Component: AiFeedbackWidget,
  },

  // --- Insights ---
  {
    type: "skill_radar",
    title: "Skill profile",
    description: "Five-axis radar: speed, control, altitude, agility, range.",
    category: "performance",
    kind: "chart",
    sizes: { m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconRadar,
    Component: SkillRadarWidget,
  },
  {
    type: "mission_progress",
    title: "Mission progress",
    description: "Best lap trend for Racing - Basics with personal-best overlay.",
    category: "performance",
    kind: "chart",
    sizes: { m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconMissionProgress,
    Component: MissionProgressWidget,
  },
  {
    type: "altitude_profile",
    title: "Altitude profile",
    description: "Altitude over time for your most recent flight.",
    category: "performance",
    kind: "chart",
    sizes: { m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconAltitudeLine,
    Component: AltitudeProfileWidget,
  },

  // --- Role-specific ---
  {
    type: "leaderboard",
    title: "Leaderboard",
    description: "Top pilots with you highlighted. L adds mission picker + details.",
    category: "instructor",
    roles: ["pilot", "instructor", "admin"],
    sizes: { m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconTrophy,
    Component: LeaderboardWidget,
  },
  {
    type: "system_status",
    title: "System status",
    description: "Ready vs failed flights, success rate.",
    category: "admin",
    roles: ["admin"],
    sizes: { s: ST.s, m: ST.m },
    defaultSize: "s",
    Icon: IconServer,
    Component: SystemStatusWidget,
  },
];

// Defaults are curated per role and fill the 12-col grid cleanly (no empty stripes).
// Pattern: L rows (12), then S groups of 4×3 (=12), then M pairs (6+6=12).
export const DEFAULT_LAYOUT_BY_ROLE: Record<string, string[]> = {
  pilot: [
    "last_flight",        // L (12)
    "flight_count",       // S  (3)
    "total_hours",        // S  (3)
    "streak",             // S  (3)
    "mission_count",      // S  (3)  => row
    "activity_heatmap",   // M (6)
    "ai_feedback",        // M (6)  => row
    "skill_radar",        // M (6)
    "mission_progress",   // M (6)  => row
    "altitude_profile",   // M (6)
    "recent_flights",     // M (6)  => row
    "leaderboard",        // M (6)  => half row (user can extend to L)
  ],
  instructor: [
    "last_flight",
    "flight_count",
    "total_hours",
    "mission_count",
    "streak",
    "activity_heatmap",
    "leaderboard",
    "skill_radar",
    "recent_flights",
    "ai_feedback",
    "mission_progress",
    "altitude_profile",
  ],
  admin: [
    "last_flight",
    "flight_count",
    "total_hours",
    "mission_count",
    "system_status",
    "activity_heatmap",
    "leaderboard",
    "recent_flights",
    "ai_feedback",
  ],
};
